import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { usePrograms, useSection, resolveUrl } from "../lib/useCMS";
import { PageShell } from "../components/PageShell";
import { PageHero } from "../components/page/PageHero";
import { SEO } from "../components/page/SEO";
import { slugifyTitle } from "../lib/slug";
import { useInquiry, isInquiryCTA } from "../lib/InquiryContext";

const FALLBACK = {
  meta_title: "Programs | Built Different Hockey",
  meta_description: "Defensemen-specific programs from Built Different Hockey: on-ice skill development, on-ice training, and individualized BDH Hockey Development.",
  hero_overline: "Programs",
  hero_heading: "Proven Ways to Get Better.",
  hero_subheading: "",
  hero_image_url: "",
  intro_body: "",
};

export default function ProgramsOverviewPage() {
  const p = useSection("page_programs", FALLBACK);
  const programs = usePrograms([]);
  const { openInquiry } = useInquiry();

  return (
    <PageShell>
      <SEO title={p.meta_title} description={p.meta_description} canonical="/programs" image={p.hero_image_url} />
      <PageHero overline={p.hero_overline} heading={p.hero_heading} subheading={p.hero_subheading} image={p.hero_image_url} />

      {p.intro_body && (
        <section className="bg-[#0a0a0a] py-16 md:py-20 border-b border-white/5">
          <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16">
            <p className="text-lg md:text-xl text-zinc-300 leading-relaxed whitespace-pre-line">{p.intro_body}</p>
          </div>
        </section>
      )}

      <section className="bg-[#0f0f0f] py-20 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {programs.map((pr, i) => {
              const slug = pr.slug || slugifyTitle(pr.title);
              return (
                <motion.div key={pr.id || i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }} className="bento-card overflow-hidden relative group flex flex-col">
                  {pr.image_url && (
                    <div className="aspect-[16/9] relative overflow-hidden">
                      <img src={resolveUrl(pr.image_url)} alt={pr.title} loading="lazy" decoding="async" className="h-full w-full object-cover opacity-50 group-hover:opacity-70 transition-opacity duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                      <div className="absolute top-5 left-5 right-5 flex items-center">
                        <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-light">{pr.tag}</span>
                      </div>
                    </div>
                  )}
                  <div className="p-7 md:p-8 flex-1 flex flex-col">
                    <h3 className="font-display uppercase text-2xl md:text-3xl leading-tight">{pr.title}</h3>
                    <p className="text-sm text-zinc-400 mt-3 leading-relaxed flex-1">{pr.description}</p>
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                      <Link to={`/programs/${slug}`} className="btn-ghost !py-2.5 !px-4 !text-xs">Learn More</Link>
                      {pr.cta_url && (
                        isInquiryCTA(pr.cta_url) ? (
                          <button
                            type="button"
                            onClick={() => openInquiry(pr)}
                            className="btn-primary !py-2.5 !px-4 !text-xs"
                            data-testid={`programs-overview-inquire-${slug}`}
                          >
                            {pr.cta_label} <ArrowUpRight className="h-3.5 w-3.5" />
                          </button>
                        ) : (
                          <a href={pr.cta_url} target={pr.cta_url.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="btn-primary !py-2.5 !px-4 !text-xs">
                            {pr.cta_label} <ArrowUpRight className="h-3.5 w-3.5" />
                          </a>
                        )
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
